import constants from './constants';
import experiment from 'experiment';
import { overridable, editorModel } from '@wix/santa-editor-utils';
import * as util from '#packages/util';
import type { PlatformOrigin } from '#packages/platform';
import type { CompStructure } from 'types/documentServices';
import * as tokenService from '../services/tokenService';
import type { EditorAPI } from '#packages/editorAPI';
import { isPlatformOriginAPP } from '../bi/bi';
import { InstallationOriginType, EditorType } from '@wix/platform-editor-sdk';
import type {
  AppData,
  CompRef,
  EditorClientSpecMapEntry,
  TPAWidget,
  MonitoringComponent,
  PagesData,
  ComponentInCsm,
  TpaInnerRouteObject,
  DynamicPageLink,
} from '@wix/document-services-types';
import { translate } from '#packages/i18n';
import { utils as coreUtils } from '#packages/core';
import type {
  HostMonitoringContext,
  AppMonitoringConfig,
} from '@wix/monitoring-types';
import { ComponentType } from '@wix/ambassador-devcenter-v1-component/types';
import type { UnifiedPage } from '@wix/ambassador-devcenter-v1-component/types';
import { pagesAPI } from '#packages/pages';
import * as stateManagement from '#packages/stateManagement';

interface MonitoringQueryParam {
  config: AppMonitoringConfig;
  context: HostMonitoringContext;
}

function makeMonitoringParam(
  appData?: AppData,
): MonitoringQueryParam | undefined {
  const monitoringComponent: MonitoringComponent = appData?.components?.find(
    (component): component is MonitoringComponent =>
      component.type === 'MONITORING',
  );

  const monitoringConfig = monitoringComponent?.data;

  if (!monitoringConfig) {
    return;
  }

  return {
    config: monitoringConfig as AppMonitoringConfig,
    context: {
      appId: appData.appDefinitionId,
      appInstanceId: appData.instanceId,
      appVersion: appData.version,
      tenantType: 'SITE',
      tenantId: editorModel.metaSiteId,
      platform: 'SITE_EDITOR',
    },
  };
}

function addSDKVersionToUrl(url: string): string {
  const sdkAbsoluteURL = `${window.serviceTopology.scriptsLocationMap['js-platform-editor-sdk']}/lib/editorSDK.min.js`;

  let sdkNumericVersion;

  if (sdkAbsoluteURL.startsWith(util.serviceTopology.scriptsDomainUrl)) {
    /*
      application could already baked semver-ish editorSDK version into the panel's url
      while not being able to handle a url-ish one (e.g. `&sdkVersion=https://localhost:3000/lib/editorSDK`)

      https://wix.slack.com/archives/CQGJP31CM/p1617619878102500

      because of that, we try to use numeric `editorSDK` version when possible,
      so panel will work properly for users and fail for developers when they'll use url-ish `&sdkVersion`

      when it will fail for developer, application needs to be tweaked to use `@wix/platform-editor-sdk/lib/loader`
      (instead of hard-coded `&sdkVersion` handling). contact #editor-platform-dev in case you need help with that
    */

    sdkNumericVersion = sdkAbsoluteURL.match(
      /\/js-platform-editor-sdk\/(?<version>\d+\.\d+\.\d+)\/lib\//i,
    )?.groups.version;
  }

  return util.url.setUrlParam(
    url,
    'sdkVersion',
    sdkNumericVersion || sdkAbsoluteURL,
  );
}

function addOverridesToUrl(url: string): string {
  const searchParams = new URLSearchParams(window.location.search);
  const artifactOverrides: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    if (key.endsWith('-override')) {
      artifactOverrides[key] = value;
    }
  });

  return util.url.setUrlParams(url, artifactOverrides);
}

function addCommonQueryParams(url: string): string {
  let urlWithCommonParams = addOverridesToUrl(url);
  urlWithCommonParams = addSDKVersionToUrl(urlWithCommonParams);
  return urlWithCommonParams;
}

function addQueryParamsToUrl(
  url: string,
  applicationId: number,
  appDefinitionId: string,
  monitoring?: MonitoringQueryParam,
): string {
  const queryParams = {
    applicationId,
    appDefinitionId,
    editorType: EditorType.Classic,
    commonConfig: JSON.stringify(window.commonConfig),
    essentials: JSON.stringify({
      language: editorModel.languageCode,
      locale: editorModel.languageCode,
      monitoring,
    }),
  };

  for (const [name, value] of Object.entries(queryParams)) {
    url = util.url.setUrlParam(url, name, value);
  }

  const extraNamespaces = getExtraNamespaces();
  if (extraNamespaces.length) {
    url = util.url.setUrlParam(
      url,
      'extraNamespaces',
      extraNamespaces.join(','),
    );
  }

  url = addCommonQueryParams(url);

  return url;
}

function getExtraNamespaces(): string[] {
  return util.appStudioUtils.isAppStudio()
    ? constants.PLATFORM_EXTRA_NAMESPACES.APP_BUILDER
    : [];
}

const getFullApplicationUrl = overridable(
  (
    appData: AnyFixMe,
    providedUrl: AnyFixMe,
    appDefinitionId: AnyFixMe,
    applicationId: AnyFixMe,
  ): string | undefined => {
    const monitoring = makeMonitoringParam(appData);
    if (providedUrl.startsWith('http')) {
      return addQueryParamsToUrl(
        providedUrl,
        applicationId,
        appDefinitionId,
        monitoring,
      );
    }

    const urlParams: {
      editorPlatformAppSources?: string;
    } = util.url.parseUrlParams(window.location.search.substr(1));

    // &editorPlatformAppSources=iframeVersion:1.1847.0,wix-code-editor-app:1.11.0
    const appSource = urlParams.editorPlatformAppSources
      ?.split(',')
      .find((part) => part.startsWith(`${appDefinitionId}:`))
      ?.split(':')[1];

    const baseUrl = getBaseUrl(appSource);

    function getBaseUrl(version: AnyFixMe) {
      const editorScriptUrl = appData?.appFields?.platform?.editorScriptUrl;
      if (editorScriptUrl) {
        return editorScriptUrl;
      }
      const name = appData?.editorArtifact;
      if (name) {
        return version
          ? util.url.joinURL(
              util.serviceTopology.scriptsDomainUrl,
              'services',
              name,
              version,
            )
          : util.serviceTopology.scriptsLocationMap[name];
      }
    }

    // TODO: replace this with a url library or add a URL polyfill
    if (baseUrl) {
      const url = new URL(providedUrl, baseUrl).href;
      return addQueryParamsToUrl(
        url,
        applicationId,
        appDefinitionId,
        monitoring,
      );
    }
  },
);

const runInContext = <T extends (...args: []) => any>(
  appDefId: string,
  editorAPI: EditorAPI,
  func: T,
): ReturnType<T> => editorAPI.dsActions.runInContext(appDefId, func);

const RESTRICTED_ORIGIN_TYPES_OPEN_APP_PANEL_ON_INSTALL = new Set([
  InstallationOriginType.ADD_PAGE_PANEL,
]);
function shouldDisableOpenAppPanelOnInstall(
  editorAPI: EditorAPI,
  platformOrigin: PlatformOrigin,
): boolean {
  if (isPlatformOriginAPP(platformOrigin)) {
    return false;
  }
  return (
    editorAPI.platform.applications.isSilentInstallRunning() ||
    RESTRICTED_ORIGIN_TYPES_OPEN_APP_PANEL_ON_INSTALL.has(
      platformOrigin?.info?.type as InstallationOriginType,
    )
  );
}

function collectPageWidgetIdsByAppIdRecursively(
  compStructure: CompStructure,
  appDefId: string,
  widgetIds: string[],
): void {
  if (
    compStructure.data?.appDefinitionId === appDefId ||
    compStructure.data?.applicationId === appDefId
  ) {
    widgetIds.push(
      compStructure?.data.controllerType || compStructure?.data.widgetId,
    );
  }
  (compStructure.components as CompStructure[])?.forEach((childStructure) => {
    collectPageWidgetIdsByAppIdRecursively(childStructure, appDefId, widgetIds);
  });
}

function getStructureWidgetIdsByAppId(
  pageStructure: CompStructure,
  appDefId: string,
): string[] {
  const widgetIds: AnyFixMe = [];
  collectPageWidgetIdsByAppIdRecursively(pageStructure, appDefId, widgetIds);
  return widgetIds;
}

function getPanelProps(
  editorAPI: EditorAPI,
  appData: AppData,
  options: AnyFixMe,
  url?: AnyFixMe,
) {
  const fullAppData = editorAPI.dsRead.platform.getAppDataByAppDefId(
    appData.appDefinitionId,
  );

  const props = Object.assign(
    { token: tokenService.generatePanelToken(fullAppData.appDefinitionId) },
    options,
  );
  if (options.url) {
    Object.assign(props, {
      url:
        url ||
        getFullApplicationUrl(
          fullAppData,
          options.url,
          fullAppData.appDefinitionId,
          fullAppData.applicationId,
        ),
    });
  }

  props.appDefinitionId = fullAppData.appDefinitionId;

  if (experiment.isOpen('se_multilingualHideOnboardingPanel')) {
    Object.assign(props, {
      appDefinitionId: fullAppData.appDefinitionId,
      instanceId: fullAppData.instanceId,
    });
  }
  return props;
}

const getSectionDeleteMessageProps = function (
  appName: string,
  defaultTitle: string,
  customContent: AnyFixMe,
) {
  return {
    headerText:
      customContent.title ?? translate(defaultTitle, { app_name: appName }),
    shouldShowIllustration: true,
    symbol: 'deleteSectionIcon_NewWorkspace',
    descriptionText: customContent.descriptionText,
    mainActionText: translate('tpa_messages_ok'),
  };
};

const displaySectionDeleteMessage = async function (
  editorAPI: EditorAPI,
  compRef: CompRef,
  appData: AppData,
) {
  const appApi = await editorAPI.platform.getAppEditorApi(
    appData.appDefinitionId,
  );
  const customContent = await appApi?.getCustomCannotDeletePanelContent({
    compId: compRef.id,
  });
  const defaultTitle = 'tpa_messages_oops_title';
  if (customContent && Object.keys(customContent).length > 0) {
    const panelProps = getSectionDeleteMessageProps(
      appData.appDefinitionName,
      defaultTitle,
      customContent,
    );
    editorAPI.panelManager.openPlatformPanel({
      panelName: 'platformPanels.platformConfirmationPanel',
      panelProps,
    });
  } else {
    editorAPI.panelManager.openPanel(
      'tpaPanels.message.cannotDeleteSection',
      {
        title: defaultTitle,
        appName: appData.appDefinitionName,
        appDefId: appData.appDefinitionId,
      },
      true,
    );
  }
};

const dataUpdateHook = function (editorAPI: EditorAPI, compRef: CompRef): void {
  const compType = editorAPI.components.getType(compRef);
  if (compType === 'wysiwyg.viewer.components.WRichText') {
    coreUtils.componentSourceFeatureUtils.updateComponentSource(editorAPI, {
      compRef,
      source: 'application',
    });
  }
};

function getPageIdByExtensionIdAndAppDefinitionId(
  editorAPI: EditorAPI,
  appDefinitionId: string,
  extensionId: string,
): string | undefined {
  const app: EditorClientSpecMapEntry = editorAPI.dsRead.platform
    .getInstalledEditorApps()
    .find((app) => app.appDefinitionId === appDefinitionId);

  if (!app) {
    return;
  }

  const tpaPageId: string | undefined = getTpaPageIdFromApp(app, extensionId);
  if (!tpaPageId) {
    return;
  }

  const pagesDataWithPopups: PagesData[] =
    pagesAPI.getAllPagesAndLightboxes(editorAPI);

  const pageData = pagesDataWithPopups.find(
    (page) =>
      page.tpaPageId === tpaPageId &&
      (page?.managingAppDefId === appDefinitionId ||
        page?.appDefinitionId === appDefinitionId),
  );

  return pageData?.id;
}

function getTpaPageIdFromComponent(
  component: ComponentInCsm,
): string | undefined {
  const compType = component.type;

  switch (compType) {
    case ComponentType.UNIFIED_PAGE: {
      const unifiedPage = component?.data as UnifiedPage;
      return unifiedPage?.base?.id;
    }
    case ComponentType.PAGE_OUT_OF_IFRAME:
      return component.name;
    case ComponentType.UNIFIED_LIGHTBOX:
      return component.componentId;
    default:
      return;
  }
}

function getTpaPageIdFromApp(
  app: EditorClientSpecMapEntry,
  extensionId: string,
): string | undefined {
  const widget: TPAWidget = app.widgets?.[extensionId];

  if (widget?.appPage?.id) {
    return widget.appPage.id;
  }

  const component: ComponentInCsm = app.components.find(
    ({ componentId }) => componentId === extensionId,
  );

  return component ? getTpaPageIdFromComponent(component) : undefined;
}

export const getSubPagesTpaInnerRoutesForPageAndApp = async (
  editorAPI: EditorAPI,
  appDefinitionId: string,
  pageId: string,
): Promise<TpaInnerRouteObject[]> => {
  const state: stateManagement.EditorState = editorAPI.store.getState();
  const subPagesInnerRoutes: stateManagement.TpaSubPagesInnerRoutes =
    state.tpaDynamicPages?.cachedInnerRoutes?.[appDefinitionId]?.pages?.[pageId]
      ?.subPages;

  if (
    subPagesInnerRoutes &&
    Object.values(subPagesInnerRoutes).some((r) => r?.length)
  ) {
    return Object.values(subPagesInnerRoutes).flat();
  }

  return await new Promise<TpaInnerRouteObject[]>((resolve) => {
    editorAPI.store.dispatch(
      stateManagement.tpaDynamicPages.actions.fetchPageInnerRoutesInState(
        pageId,
        undefined,
        (innerRoutes) => {
          resolve(innerRoutes);
        },
      ),
    );
  });
};

export const getTpaInnerRouteBySlug = async (
  editorAPI: EditorAPI,
  appDefinitionId: string,
  pageId: string,
  slug: string,
): Promise<TpaInnerRouteObject> => {
  const allSubPagesTpaInnerRoutes: TpaInnerRouteObject[] =
    await getSubPagesTpaInnerRoutesForPageAndApp(
      editorAPI,
      appDefinitionId,
      pageId,
    );

  const tpaInnerRoute: TpaInnerRouteObject = allSubPagesTpaInnerRoutes.find(
    (entry: TpaInnerRouteObject) => {
      const pathParts = entry.path?.split('/');
      return pathParts?.[pathParts.length - 1] === slug;
    },
  );

  return tpaInnerRoute;
};

function createDynamicPageLink(
  editorAPI: EditorAPI,
  {
    pageId,
    slug,
    innerRoute,
  }: {
    pageId: string;
    slug: string;
    innerRoute: TpaInnerRouteObject;
  },
): DynamicPageLink {
  const dynamicPageLink: DynamicPageLink =
    editorAPI.dsRead.data.createItem('DynamicPageLink');
  const routerId: string =
    editorAPI.dsRead.routers.getRouterDataForPageIfExist(pageId)?.routerId;

  if (routerId) {
    dynamicPageLink.routerId = routerId;
    dynamicPageLink.innerRoute = slug;
  } else {
    dynamicPageLink.routerId = pageId;
    dynamicPageLink.innerRoute = `.${innerRoute.path}`;
    dynamicPageLink.isTpaRoute = true;
  }

  return dynamicPageLink;
}

export {
  displaySectionDeleteMessage,
  getExtraNamespaces,
  getFullApplicationUrl,
  runInContext,
  addCommonQueryParams,
  getStructureWidgetIdsByAppId,
  shouldDisableOpenAppPanelOnInstall,
  getPanelProps,
  dataUpdateHook,
  getTpaPageIdFromComponent,
  getTpaPageIdFromApp,
  getPageIdByExtensionIdAndAppDefinitionId,
  createDynamicPageLink,
};
